import type { ReactNode } from 'react';

// Sovereign Emerald palette
const SE = {
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  champagneRing: 'rgba(201,169,97,0.45)',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  dotted:        'rgba(11,59,46,0.18)',
  labelGold:     '#A08A55',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

const EditPencil = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

/** Boxless section: uppercase title, champagne rule stretching right, optional Edit pill. */
export function LedgerSection({
  title, subtitle, onEdit, children,
}: {
  title: string;
  subtitle?: string;
  onEdit?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="px-1">
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          <h4 className="text-[12.5px] font-bold uppercase tracking-[0.14em]" style={{ color: SE.title }}>
            {title}
          </h4>
          {subtitle && (
            <p className="mt-0.5 text-[11px]" style={{ color: SE.muted }}>{subtitle}</p>
          )}
        </div>
        <div className="h-px min-w-6 flex-1" style={{ background: `linear-gradient(to right, ${SE.champagneRing}, transparent)` }} />
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold transition-colors duration-200"
            style={{ backgroundColor: SE.champagneWash, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash; }}
          >
            <EditPencil />
            Edit
          </button>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-x-10 lg:grid-cols-2">{children}</div>
    </section>
  );
}

/** Definition-list row: micro champagne label over ink value, dotted hairline underneath. */
export function LedgerField({
  label, value, fullWidth,
}: {
  label: string;
  value?: ReactNode;
  fullWidth?: boolean;
}) {
  const empty = value === undefined || value === null || value === '';
  return (
    <div className={`py-3 ${fullWidth ? 'lg:col-span-2' : ''}`} style={{ borderBottom: `1px dotted ${SE.dotted}` }}>
      <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: SE.labelGold }}>
        {label}
      </p>
      <div className="mt-1 break-words text-[15px] font-medium" style={{ color: SE.ink }}>
        {empty ? <span className="font-normal italic" style={{ color: SE.muted }}>Not provided</span> : value}
      </div>
    </div>
  );
}
